import { useSelector } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';

import { CustomsHelpMessage } from './customs-help-message';
import { Carriers } from '../../../../../constants/carriers';
import { emptyObject } from 'ui-components/default-props/empty';
import { CUSTOMERS_CREATE_LABEL, RATE_BROWSER } from '../../../../../enums/csw-types';
import { useIOR } from '../../../../../hooks/use-ior';
import { useTaxIdentifiers } from '../../../../../hooks/use-tax-identifier';
import Global from '../../../../../store/global-selectors';
import { CustomsContentType } from '../../../components/content-type-dropdown';
import { CustomsDutiesPaid } from '../../../components/duties-paid';
import { CustomsExportDeclarationNumber } from '../../../components/export-declaration-number-input';
import { CustomsInvoiceNumber } from '../../../components/invoice-number-input/invoice-number-input';
import { CustomsIOR } from '../../../components/customs-ior';
import { ItnOrExemption } from '../../../components/itn-or-exemption-input';
import { CustomsNonDelivery } from '../../../components/non-delivery-dropdown';
import { CustomsPostagePaid } from '../../../components/postage-paid';
import { CustomsTaxIdentifiers } from '../../../components/tax-identifiers';
import { useCustomsContext } from '../../../context/context';

import commonStyle from '../../../components/common-style.module.less';
import { messages } from './messages';

const getConfigurationMessage = (hasTaxIdentifiers: boolean, hasIOR: boolean) => {
  if (!hasTaxIdentifiers) {
    return hasIOR ? messages.noTaxIdentifiersOnly : messages.noTaxIdentifiersAndIOR;
  }
  if (!hasIOR) {
    return messages.noIOROnly;
  }
  return null;
};

const isEntityFulfillmentPlan = (fulfillmentPlanId: string | null | undefined) => {
  if (!fulfillmentPlanId) {
    return false;
  }
  return ![RATE_BROWSER, CUSTOMERS_CREATE_LABEL].includes(fulfillmentPlanId);
};

const isItnOrAesEligible = (carrierId: string) => {
  return [Carriers.USPS.carrierId, Carriers.UPS.carrierId, Carriers.FEDEX.carrierId].includes(carrierId);
};

export const CustomsWidgetHeader = () => {
  const { fulfillmentPlanId } = useCustomsContext();
  const { activeIORs } = useIOR();
  const { taxIdentifiers } = useTaxIdentifiers({ includeInactive: false });
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const { carrierId } =
    useSelector((state) =>
      Global.selectSellerServiceById(state, {
        serviceId: fulfillmentPlan?.serviceId,
      }),
    ) ?? emptyObject;

  const isEntityFP = isEntityFulfillmentPlan(fulfillmentPlanId);
  const showTaxIdentifiers = taxIdentifiers.length > 0 && isEntityFP;
  const showIORDropdown = activeIORs.length > 0 && isEntityFP;
  const isItnOrAes = showTaxIdentifiers && carrierId && isItnOrAesEligible(carrierId);
  const helpMessage = getConfigurationMessage(showTaxIdentifiers, showIORDropdown);

  return (
    <div className={commonStyle['widget-section']}>
      <FlexColumn gap={Gap.Space8}>
        <CustomsContentType />
        <CustomsNonDelivery />
        <CustomsDutiesPaid />
        <CustomsPostagePaid />
        <CustomsExportDeclarationNumber />
        <CustomsInvoiceNumber />
        {isItnOrAes && <ItnOrExemption fulfillmentPlan={fulfillmentPlan} isUsps={carrierId === Carriers.USPS.carrierId} />}
        {showTaxIdentifiers && <CustomsTaxIdentifiers />}
        {showIORDropdown && <CustomsIOR />}
        {!!helpMessage && <CustomsHelpMessage helpMessage={helpMessage} />}
      </FlexColumn>
    </div>
  );
};
